import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router';
import { useCategories } from '../api/categories';
import { useInventoryHolds } from '../api/inventory';
import type { Product } from '../api/products';
import { useProducts } from '../api/products';
import type { ByProductRow } from '../api/reports';
import { useReportByProduct } from '../api/reports';
import { LastUpdated } from '../components/LastUpdated';
import { Money } from '../components/Money';
import { InventoryHistoryDrawer } from './InventoryHistoryDrawer';
import { InventoryHoldsDrawer } from './InventoryHoldsDrawer';
import type { SalesContext } from './Sales';

interface CategoryGroup {
  /** Stable key for React: category UUID, or 'custom' for the catch-all bucket. */
  key: string;
  name: string;
  /** Catalog sort. 'custom' sorts after all real categories. */
  sort: number;
  rows: Array<ByProductRow & { product?: Product }>;
}

const CUSTOM_GROUP_SORT = Number.MAX_SAFE_INTEGER;

/**
 * Build category-grouped rows in catalog order. Within each group, products
 * are sorted by Product.sort (ascending) then name. Rows for productIds we
 * can't find in the catalog (e.g. archived since the sale) fall back to the
 * report's own ordering and use Number.MAX_SAFE_INTEGER as their sort key so
 * they trail other in-catalog rows in the same group.
 */
function buildGroups(
  rows: ByProductRow[] | undefined,
  products: Product[] | undefined,
  categories: { id: string; name: string; sort: number }[] | undefined,
): CategoryGroup[] {
  if (!rows) return [];
  const productById = new Map((products ?? []).map((p) => [p.id, p]));
  const categoryById = new Map(
    (categories ?? []).map((c) => [c.id, { name: c.name, sort: c.sort }]),
  );
  const groups = new Map<string, CategoryGroup>();

  const ensureGroup = (key: string, name: string, sort: number): CategoryGroup => {
    let g = groups.get(key);
    if (!g) {
      g = { key, name, sort, rows: [] };
      groups.set(key, g);
    }
    return g;
  };

  for (const row of rows) {
    const product = row.productId ? productById.get(row.productId) : undefined;
    const categoryId = row.categoryId ?? product?.categoryId ?? null;
    const cat = categoryId ? categoryById.get(categoryId) : null;
    if (categoryId && cat) {
      ensureGroup(categoryId, cat.name, cat.sort).rows.push({ ...row, product });
    } else {
      // No category in catalog (custom amount) → bucket at the bottom.
      ensureGroup('custom', 'Custom amounts', CUSTOM_GROUP_SORT).rows.push({
        ...row,
        product,
      });
    }
  }

  const ordered = [...groups.values()].sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name));
  for (const g of ordered) {
    g.rows.sort((a, b) => {
      const sa = a.product?.sort ?? Number.MAX_SAFE_INTEGER;
      const sb = b.product?.sort ?? Number.MAX_SAFE_INTEGER;
      return sa - sb || a.name.localeCompare(b.name);
    });
  }
  return ordered;
}

export function SalesByProduct() {
  const { eventId, range } = useOutletContext<SalesContext>();
  const report = useReportByProduct(eventId, range);
  const products = useProducts(eventId);
  const categories = useCategories(eventId);
  const holds = useInventoryHolds(eventId);
  const [openProduct, setOpenProduct] = useState<{ productId: string; name: string } | null>(
    null,
  );
  const [openHolds, setOpenHolds] = useState<{ productId: string; name: string } | null>(null);

  const holdsByProductId = useMemo(() => {
    const m = new Map<string, { quantity: number; transactionCount: number }>();
    for (const h of holds.data ?? []) {
      m.set(h.productId, {
        quantity: h.heldQuantity,
        transactionCount: h.heldTransactionCount,
      });
    }
    return m;
  }, [holds.data]);

  const groups = useMemo(
    () => buildGroups(report.data, products.data, categories.data),
    [report.data, products.data, categories.data],
  );

  const isLoading = report.isLoading || products.isLoading || categories.isLoading;
  const isError = report.isError;

  if (isLoading) return <div style={{ color: 'var(--fg-3)' }}>Loading…</div>;
  if (isError || !report.data) return <div className="form-error">Couldn't load product report.</div>;
  if (report.data.length === 0) {
    return (
      <div>
        <div style={{ color: 'var(--fg-3)' }}>No sales in this window.</div>
        <LastUpdated dataUpdatedAt={report.dataUpdatedAt} />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'grid', gap: 16 }}>
        {groups.map((g) => (
          <div key={g.key} className="card" style={{ overflow: 'hidden' }}>
            <div
              style={{
                padding: '8px 12px',
                fontSize: 12,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                color: 'var(--fg-3)',
                borderBottom: '1px solid var(--border-1)',
              }}
            >
              {g.name}
            </div>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="num">Qty sold</th>
                  <th className="num">Gross</th>
                  <th className="num">Refunded</th>
                  <th className="num">Net</th>
                  <th className="num">Remaining</th>
                </tr>
              </thead>
              <tbody>
                {g.rows.map((row) => {
                  const tracked = row.product?.inventoryTracked === true;
                  const remaining = tracked ? (row.product?.inventoryRemaining ?? 0) : null;
                  const held = row.productId ? holdsByProductId.get(row.productId) : undefined;
                  return (
                    <tr key={row.productId ?? `custom:${row.name}`}>
                      <td>{row.name}</td>
                      <td className="num">{row.qtySold}</td>
                      <td className="num">
                        <Money value={row.grossCents / 100} />
                      </td>
                      <td className="num">
                        <Money value={row.refundedCents / 100} />
                      </td>
                      <td className="num">
                        <Money value={row.netCents / 100} />
                      </td>
                      <td className="num">
                        {tracked && row.productId ? (
                          <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                            <button
                              type="button"
                              className="btn-link"
                              onClick={() =>
                                setOpenProduct({ productId: row.productId!, name: row.name })
                              }
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                color: 'var(--oeg-purple)',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                font: 'inherit',
                              }}
                              aria-label={`Inventory history for ${row.name}`}
                            >
                              {remaining}
                            </button>
                            {held && held.quantity > 0 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setOpenHolds({ productId: row.productId!, name: row.name })
                                }
                                title="Units held by transactions that haven't completed (pending, canceled, voided). Click to review and optionally release."
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  color: 'var(--oeg-warning, #c98a2a)',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  font: 'inherit',
                                  fontSize: 11,
                                }}
                                aria-label={`${held.quantity} units held; review`}
                              >
                                {held.quantity} held
                              </button>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--fg-4)' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>
      <div style={{ padding: 12, color: 'var(--fg-4)', fontSize: 11 }}>
        Refunds allocated proportionally by line dollar value. Click a tracked
        Remaining count to see its inventory history.
      </div>
      <LastUpdated dataUpdatedAt={report.dataUpdatedAt} />

      {openProduct && (
        <InventoryHistoryDrawer
          eventId={eventId}
          product={openProduct}
          onClose={() => setOpenProduct(null)}
        />
      )}

      {openHolds && (
        <InventoryHoldsDrawer
          eventId={eventId}
          product={openHolds}
          onClose={() => setOpenHolds(null)}
        />
      )}
    </div>
  );
}
